// Copyright (c) 2024 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import type { JSX } from "react";
import { connect } from "react-redux";
import Select, {
  components,
  ControlProps,
  DropdownIndicatorProps,
  MenuListProps,
  NoticeProps,
  OptionProps,
  StylesConfig,
} from "react-select";
import { defineMessages, FormattedMessage, IntlContext, IntlShape } from "react-intl";
import { MessageDescriptor } from "react-intl";

import { IState } from "states/states";
import { IAction } from "actions";
import { isSet } from "shared/utils/utils";
import { ITariffCircuit } from "shared/types/parameter";

import IconTreeMore from "icons/iconDdTreeMore";
import IconTreeLess from "icons/iconDdTreeLess";
import IconTreeMiddle from "icons/iconDdTreeMiddle";
import IconTreeEnd from "icons/iconDdTreeEnd";
import IconDDSelNone from "icons/iconDdSelNone";
import IconDDSelSelected from "icons/iconDdSelSelected";
import IconDDSelMultiple from "icons/iconDdSelMultiple";

import IconDDCircleNone from "icons/circle";
import IconDDCircleSelected from "icons/circleCheck";
import IconDDCircleMultiple from "icons/circleDot";

import SwitchButtonOn from "icons/switchButtonOn";
import SwitchButtonOff from "icons/switchButtonOff";

import cssContent from "constants.module.scss";
import "./TreeViewDropDown.scss";

const NO_INFO_STORED = -10000;

// HierarchySelectionState defines the selection state of a hierarchy item
export enum HierarchySelectionState {
  // the item is not selected
  UNSELECTED,
  // the item is selected
  SELECTED,
  // the item itself is not selected but has children selected
  SOMESELECTED,
  // in hierarchy mode: the item is "selected" because its a child of a selected parent (node)
  SELECTED_CHILD,
}

// ITreeViewDropDownOption defines the structure of an item in the dropdown
export type ITreeViewDropDownOption = {
  // the "id" of the entry in the hierarchy
  // the array length defines the hierarchy level
  // the item of each array entry defines the index of the item in the hierarchy
  entryId: number[];
  // the text to display in the dropdown or in any chart
  text: string;
  // the color to display in the dropdown or in any chart
  color: string;
  // defines if after this entry in the hierarchy there are more entries (needed for visualization inside treeview)
  isEndNode: boolean;
  // reference to the "real" item in the hierarchy (e.g. TariffCircuit)
  referenceEntry?: ITariffCircuit;
  // defines if the item is a leaf (no children) or not
  hasChildren: boolean;
  // the checked state of the item (used for visualization in dropdown and in charts)
  checked: HierarchySelectionState;
};

// ITreeViewDropDownExtProps defines the external properties for the TreeViewDropDown component
interface ITreeViewDropDownExtProps {
  // options to display in the dropdown
  // items must be of type ITreeViewDropDownOption[]
  options: ITreeViewDropDownOption[];

  // [optional] a css class name which should get additionaly applied to the root-element of the component
  className?: string;
  // [optional] the height of each item in the dropdown
  //    default: 34px
  height?: number;
  // [optional] the placeholder text to display when no option is selected
  //    default: "Select an item"
  placeholder?: string;
  // [optional] the placeholder text to display when no option is available
  //    default: "No options available"
  placeholderNoOption?: string;
  // [optional] defines if the dropdown-menu should open left or right aligned to the parent dropdown-button
  //    default: "left"
  searchText?: string;
  // [optional] the text to display in the empty search box
  menuOrientation?: "left" | "right";
  // [optional] defines if the dropdown should offer hierarchy mode
  //    default: false (normal tree view)
  isHierarchyMode?: boolean;
  // [optional] callback when the hierarchy mode has been changed
  //    parameter isSelected defines if the hierarchy mode is now selected or not
  onHierarchyModeChanged?: (isHierarchyMode: boolean) => number | undefined;
  // [optional] callback when the selected items have changed
  //    parameter selectedItems provides a list of the currently selected items
  onItemsChanged?: (selectedItems: ITreeViewDropDownOption[]) => void;
  // [optional] callback to render tool-tip
  renderToolTip?: (currentItem: ITreeViewDropDownOption) => string | undefined;
}

interface ITreeViewDropDownProps {}

interface ITreeViewDropDownState {
  // input text for filtering the items
  inputText: string;
  // opened items in the tree
  opened: number[][];
  open: boolean;
}

const mapStateToProps = (state: IState, ownProps: ITreeViewDropDownExtProps) => {
  const props: ITreeViewDropDownProps = {};

  return props;
};

interface ITreeViewDropDownDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ITreeViewDropDownDispatch = {};
  return dispatchProps;
};

const messages = defineMessages({
  selectAll: {
    id: "Select All",
  },
  hierarchyView: {
    id: "Hierarchy view",
  },
  defaultPlaceholder: {
    id: "Select an item",
  },
  inputPlaceholder: {
    id: "Type to filter items",
  },
  allItemsSelected: {
    id: "All items selected",
  },
  itemsSelected: {
    id: "items selected",
  },
});

class TreeViewDropDownComponent extends React.Component<
  ITreeViewDropDownProps & ITreeViewDropDownExtProps & ITreeViewDropDownDispatch,
  ITreeViewDropDownState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  state: ITreeViewDropDownState = {
    inputText: "",
    opened: [[NO_INFO_STORED]], // use a dummy entry to avoid empty array (which would be interpreted as "all closed")
    open: false,
  };

  private SearchBox = (props: ControlProps<ITreeViewDropDownOption>) => {
    return (
      <div
        className="searchbox d-flex"
        style={{
          margin: "6px 10px",
          height: "26px",
          backgroundColor: "white",
          borderRadius: "4px",
          border: "1px solid",
          minWidth: "250px",
        }}
      >
        <input
          className="mx-2"
          placeholder={this.props.searchText}
          style={{ border: "0", width: "100%" }}
          value={this.state.inputText}
          onChange={(event) => {
            this.setState({ inputText: event.currentTarget.value ?? "" });
          }}
        ></input>
        <div style={{ color: "black", height: 24, width: 32, margin: "auto 0" }}>
          <svg width="20" height="20" viewBox="0 0 24 24" focusable="false" role="presentation">
            <path
              d="M16.436 15.085l3.94 4.01a1 1 0 0 1-1.425 1.402l-3.938-4.006a7.5 7.5 0 1 1 1.423-1.406zM10.5 16a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11z"
              fill="currentColor"
              fillRule="evenodd"
            />
          </svg>
        </div>
      </div>
    );
  };

  private CustomMenuWithInput = (props: MenuListProps<ITreeViewDropDownOption>) => {
    const allSelected =
      this.props.options.find((option) => option.checked !== HierarchySelectionState.SELECTED) ===
      undefined;
    const noneSelected =
      this.props.options.find((option) => option.checked === HierarchySelectionState.SELECTED) ===
      undefined;

    return (
      <div>
        <div className="w-100 treeview-input cursor-default" />

        <div className="d-flex justify-content-between vertical-align-middle treeview-hierarchy-bar w-100 px-1 cursor-default">
          <div className="d-flex justify-content-left align-items-center px-2">
            {this.props.onHierarchyModeChanged !== undefined && (
              <div
                className="hierarchy-switch cursor-pointer"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();

                  // recalculate the options
                  if (this.props.isHierarchyMode === false) {
                    // we switch now from hierarchy to single view
                    // only select the first hierarchy which is selected...
                    const availableItem = this.props.options.find(
                      (entry) =>
                        (entry.hasChildren === true || !isSet(entry.referenceEntry)) &&
                        entry.checked === HierarchySelectionState.SELECTED,
                    );
                    // now add all from from one level lower
                    const level = (availableItem?.entryId.length ?? 0) + 1;
                    const selectedItems = this.props.options.filter(
                      (entry) =>
                        this.isChildOfParent(entry.entryId, availableItem?.entryId ?? []) &&
                        entry.entryId.length === level,
                    );
                    selectedItems.forEach((entry) => {
                      entry.checked = HierarchySelectionState.SELECTED;
                    });
                    const selectedOptions: ITreeViewDropDownOption[] =
                      isSet(availableItem) ? [availableItem, ...selectedItems] : [];

                    this.props.onItemsChanged?.(selectedOptions);
                  }

                  // inform derrived class that the Hierarchy-Mode was changed (parameter -> inverted current mode)
                  const selectedRootId = this.props.onHierarchyModeChanged?.(
                    !this.props.isHierarchyMode,
                  );

                  // only open/close root-item selection in Hierarchy-Mode
                  if (this.props.isHierarchyMode === false) {
                    const selectedRoot =
                      isSet(selectedRootId) ?
                        this.props.options.find(
                          (entry) =>
                            (entry.referenceEntry?.id ?? entry.entryId.at(-1)) === selectedRootId,
                        )
                      : undefined;

                    if (isSet(selectedRoot)) {
                      this.openAllParent(selectedRoot);
                      const newOpenOptionList = this.closeAllFirstChildren(selectedRoot);
                      this.setState({ opened: newOpenOptionList });
                    }
                  }
                }}
              >
                {this.props.isHierarchyMode === true ?
                  <SwitchButtonOn />
                : <SwitchButtonOff />}
              </div>
            )}
            <div className="ms-1">
              <FormattedMessage
                id={
                  this.props.onHierarchyModeChanged !== undefined ?
                    messages.hierarchyView.id
                  : messages.selectAll.id
                }
              />
            </div>
          </div>

          {this.props.isHierarchyMode === false && (
            <div
              className="pe-3 cursor-pointer"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (allSelected) {
                  // deselect all
                  this.props.options.forEach((value) => {
                    value.checked = HierarchySelectionState.UNSELECTED;
                  });
                  this.props.onItemsChanged?.(this.props.options);
                } else {
                  this.props.options.forEach(
                    (entry) => (entry.checked = HierarchySelectionState.SELECTED),
                  );
                  this.props.onItemsChanged?.(this.props.options);
                }
              }}
            >
              {allSelected === true ?
                <IconDDSelSelected />
              : noneSelected === true ?
                <IconDDSelNone />
              : <IconDDSelMultiple />}
            </div>
          )}
        </div>

        <div className="treeview-items pt-1 pb-1">{props.children}</div>
      </div>
    );
  };

  private onChange(
    action: "deselect-option" | "select-option",
    event: React.MouseEvent,
    option: ITreeViewDropDownOption,
    directSelect: boolean,
  ) {
    // **************
    // HIERARCHY MODE
    // **************
    // do not allow and forward selecting end-nodes in hierarchy mode
    if (this.props.isHierarchyMode === true) {
      if (
        (option.checked === HierarchySelectionState.SELECTED_CHILD &&
          option.hasChildren === false) ||
        (option.hasChildren === false && directSelect === true) ||
        (option.checked === HierarchySelectionState.SELECTED && directSelect === true)
      ) {
        // do not update if an already selected child was clicked
        // do not update if an already selected node was selected again (direclty)
        return;
      }
      // otherwise -> select the parent
      // if the option has children, we select all children
      // and set their parent to SELECTED
      const parentEntryId = [...option.entryId];
      directSelect === false && option.hasChildren === false && parentEntryId.splice(-1, 1);
      const parent =
        this.props.options.find((item) => this.equalEntryId(item.entryId, parentEntryId)) ?? option;

      if (parent.checked === HierarchySelectionState.SELECTED && directSelect === false) {
        // do not update if the newly selected parent was already set
        return;
      }

      this.props.options.forEach((entry) => {
        entry.checked = HierarchySelectionState.UNSELECTED;
      });

      this.props.options
        .filter((entry) => {
          return (
            entry.entryId.length === parent.entryId.length + 1 &&
            this.isChildOfParent(entry.entryId, parent.entryId)
          );
        })
        .forEach((entry) => {
          entry.checked = HierarchySelectionState.SELECTED_CHILD;
        });

      parent.checked = HierarchySelectionState.SELECTED;
      const newOpenOptionList = this.closeAllFirstChildren(parent);
      this.setState({ opened: newOpenOptionList });
    }
    // ***********
    // NORMAL MODE
    // ***********
    else {
      // check if the user clicken an energy-node
      if (option.referenceEntry !== undefined) {
        // check if the node should get deselected
        if (action === "deselect-option") {
          // node should get deselected
          // check if the user holds down shift
          if (event.shiftKey === true) {
            // deselect all sub-children
            this.props.options
              .filter((entry) =>
                (option.entryId as number[]).every(
                  (subid, index) => entry.entryId[index] === subid,
                ),
              )
              .forEach((entry) => (entry.checked = HierarchySelectionState.UNSELECTED));
          } else {
            // no shift-key (CTLR Click is not needed for deselecting single nodes!)
            option.checked = HierarchySelectionState.UNSELECTED;
          }
        } else if (action === "select-option") {
          // node should get selected (is unselected or has any child selected underneath)
          // -> check which type of node and which button he hold down (shift / ctlr [meta on mac])
          // but not the shift-key
          if (
            (option.checked !== HierarchySelectionState.SOMESELECTED ||
              event.ctrlKey ||
              event.metaKey) &&
            !event.shiftKey
          ) {
            // if user clicked on an unselected parent or any child-node or if he holds the ctrl-key
            // just select the node
            option.checked = HierarchySelectionState.SELECTED;
          } else {
            // else - the user clicked on an node with any selected child node underneath
            // select/unsselect all sub-children depeinding if the shif-key was hold
            const selectEntry =
              event.shiftKey === true ?
                HierarchySelectionState.SELECTED
              : HierarchySelectionState.UNSELECTED;
            this.props.options
              .filter((entry) =>
                option.entryId.every((subid, index) => entry.entryId[index] === subid),
              )
              .forEach((entry) => (entry.checked = selectEntry));
          }
        } else {
          // do not refresh the component!
          return;
        }
      } else {
        // user clicked on a energy-node -> select ALL tariffs/chargers underneath
        this.props.options
          .filter((entry) => this.isChildOfParent(entry.entryId, option.entryId))
          .forEach((entry) => {
            entry.checked =
              action === "deselect-option" ?
                HierarchySelectionState.UNSELECTED
              : HierarchySelectionState.SELECTED;
          });

        this.props.options
          .filter((entry) => this.isChildOfParent(entry.entryId, option.entryId))
          .forEach((entry) => {
            entry.checked = HierarchySelectionState.SELECTED;
          });
      }
    }

    this.props.onItemsChanged?.(this.props.options);
  }

  private openedSet = new Set<string>([NO_INFO_STORED.toString()]);

  private readonly selectStyles: StylesConfig<ITreeViewDropDownOption, false> = {
    control: (provided) => ({
      ...provided,
      minWidth: 250,
      margin: 4,
      backgroundColor: "white",
    }),
    placeholder: (provided) => ({
      ...provided,
      justifyContent: "start",
      display: "flex",
    }),
    menu: () => ({
      boxShadow: "inset 0 1px 0 rgba(0, 0, 0, 0.1)",
      backgroundColor: "white",
    }),
    option: (base, { isFocused }) => ({
      ...base,
      backgroundColor:
        isFocused ? cssContent.TREEVIEW_DROPDOWN_BACKGROUND : cssContent.DROPDOWN_TRANSPARENT,
      display: "flex",
      justifyContent: "space-between",
      cursor: "pointer",
      padding: "1px 10px",
    }),
  };

  DropdownIndicator = (props: DropdownIndicatorProps<any, any>) => (
    <div style={{ color: "black", height: 24, width: 32 }}>
      <svg width="20" height="20" viewBox="0 0 24 24" focusable="false" role="presentation">
        <path
          d="M16.436 15.085l3.94 4.01a1 1 0 0 1-1.425 1.402l-3.938-4.006a7.5 7.5 0 1 1 1.423-1.406zM10.5 16a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11z"
          fill="currentColor"
          fillRule="evenodd"
        />
      </svg>
    </div>
  );

  private NoOptionsMessage = (props: NoticeProps<ITreeViewDropDownOption>) => (
    <components.NoOptionsMessage {...props}>
      <span className="custom-css-class">{this.props.placeholderNoOption}</span>
    </components.NoOptionsMessage>
  );

  public render() {
    const selected = this.props.options.filter(
      (item) => item.checked === HierarchySelectionState.SELECTED,
    );
    const selectedCnt = selected.length;

    const allItemsAreSelected = this.props.options.length === selectedCnt;

    const selectedCntText = `${selectedCnt} ${this.context.formatMessage(messages.itemsSelected)}`;

    // if the component is called the frist time
    // we should open all items which are selected
    // the state is stored once the user opened/closed
    // any of the items
    // call this only on first call when [[NO_INFO_STORED]] is stored...
    if (this.state.opened[0]?.[0] === NO_INFO_STORED) {
      this.state.opened.pop();
      selected.forEach((item) => {
        this.openAllParent(item);
        if (this.props.isHierarchyMode === true) {
          const newOpenOptionList = this.closeAllFirstChildren(item);
          this.state.opened.push(...newOpenOptionList);
        }
      });
    }
    this.openedSet = new Set(this.state.opened.map((e) => e.join(",")));

    return (
      <this.Dropdown
        isOpen={this.state.open}
        onClose={() => this.setState({ open: false })}
        target={
          <button
            className={`btn-header dropdown-toggle btn btn-secondary h-100`}
            onClick={() => this.setState({ open: !this.state.open })}
          >
            {selectedCnt > 0 ?
              selectedCnt > 1 ?
                allItemsAreSelected ?
                  this.context.formatMessage(messages.allItemsSelected)
                : selectedCntText
              : (this.showSelectedEntry() ?? selectedCntText)
            : (this.props.placeholder ?? this.context.formatMessage(messages.defaultPlaceholder))
            }{" "}
          </button>
        }
      >
        <Select
          {...this.props}
          className={`${this.props.className} dropdownselect`}
          styles={this.selectStyles}
          components={{
            ...components,
            Option: (props) =>
              this.optionsCallback(props as OptionProps<ITreeViewDropDownOption, boolean, any>),
            DropdownIndicator: this.DropdownIndicator,
            IndicatorSeparator: null,
            NoOptionsMessage: this.NoOptionsMessage,
            MenuList: this.CustomMenuWithInput,
            Control: this.SearchBox,
          }}
          isMulti={true}
          closeMenuOnSelect={false}
          isSearchable={false}
          controlShouldRenderValue={false}
          hideSelectedOptions={false}
          isClearable={false}
          backspaceRemovesValue={false}
          blurInputOnSelect={false}
          defaultMenuIsOpen={false}
          onFocus={(event) => {
            event.stopPropagation();
            event.preventDefault();
          }}
          autoFocus={false}
          menuIsOpen={true}
          filterOption={(option, inputValue) => {
            // filter options based on input text
            return (
              inputValue === "" || option.data.text.toLowerCase().includes(inputValue.toLowerCase())
            );
          }}
          options={
            this.state.inputText !== "" ?
              this.translateOptions(this.props.options)
            : this.translateOptions(this.props.options.filter((opt) => this.shouldGetRendered(opt)))
          }
          isOptionSelected={(option) => {
            const guiOption = option as ITreeViewDropDownOption;
            const isSelected =
              guiOption.checked === HierarchySelectionState.SELECTED || allItemsAreSelected;
            return isSelected;
          }}
          tabSelectsValue={true}
          onInputChange={(newValue, actionMeta) => {
            if (actionMeta.action === "input-change" || actionMeta.action === "menu-close") {
              this.setState({ inputText: newValue });
            }
          }}
          inputValue={this.state.inputText}
          onKeyDown={(event) => {
            // do not close the dropdown on escape key if user entered some filter-text
            // but clear the input text first
            if (event.key === "Escape" && this.state.inputText !== "") {
              this.setState({ inputText: "" });
              event.stopPropagation();
              event.preventDefault();
            }
          }}
        />
      </this.Dropdown>
    );
  }

  private showSelectedEntry() {
    const selectedEntry = this.props.options.find(
      (item) => item.checked === HierarchySelectionState.SELECTED,
    );

    if (isSet(selectedEntry)) {
      if (selectedEntry.entryId.length === 1) {
        // check if selectedEntry.text is a MessageDescriptor
        return this.context.formatMessage({ id: selectedEntry.text });
      } else {
        return selectedEntry.text;
      }
    }

    return undefined;
  }

  private translateOptions(options: ITreeViewDropDownOption[]) {
    return options.map((curItem) => {
      // translate label if entry is of type MessageDescriptor
      if (isSet((curItem.text as MessageDescriptor)?.id)) {
        curItem.text = this.context.formatMessage(curItem.text as MessageDescriptor);
      }
      return curItem;
    });
  }

  private shouldGetRendered(data: ITreeViewDropDownOption) {
    // root level should always be rendered
    if (data.entryId.length === 1) {
      return true;
    }

    return this.allParentOpened(data);
  }

  private equalEntryId(entryId1: number[], entryId2: number[]): boolean {
    return (
      entryId1.length === entryId2.length &&
      entryId1.every((value, index) => value === entryId2[index])
    );
  }

  private isChildOfParent(entryId: number[], parentEntryId: number[]): boolean {
    if (entryId.length <= parentEntryId.length) {
      return false;
    }
    for (let i = 0; i < parentEntryId.length; i++) {
      if (entryId[i] !== parentEntryId[i]) {
        return false;
      }
    }
    return true;
  }

  private isDirectChildOfParent(entryId: number[], parentEntryId: number[]): boolean {
    if (entryId.length <= parentEntryId.length || entryId.length > parentEntryId.length + 1) {
      return false;
    }
    for (let i = 0; i < parentEntryId.length; i++) {
      if (entryId[i] !== parentEntryId[i]) {
        return false;
      }
    }
    return true;
  }

  private allParentOpened(data: ITreeViewDropDownOption): boolean {
    const maxIdx = data.entryId.length - 1;

    if (maxIdx === 0) {
      return this.openedSet.has(String(data.entryId[0]));
    }

    const prevEntryID = data.entryId.slice(0, maxIdx);

    const parentEntry = this.props.options.find((entry) =>
      this.equalEntryId(entry.entryId, prevEntryID),
    );

    if (parentEntry !== undefined) {
      return this.allParentOpened(parentEntry) && this.openedSet.has(parentEntry.entryId.join(","));
    } else {
      return this.openedSet.has(data.entryId.join(","));
    }
  }

  private openAllParent(data: ITreeViewDropDownOption) {
    if (isSet(data.referenceEntry?.parent) || data.entryId.length === 2) {
      // we've got a parent (or the root)...
      // prepare new EntryId to search for (parent)
      const parentEntryId = [...data.entryId];
      parentEntryId.splice(-1, 1);

      const parent = this.props.options.find((entry) =>
        this.equalEntryId(entry.entryId, parentEntryId),
      );

      if (isSet(parent)) {
        if (!isSet(this.state.opened.find((entry) => this.equalEntryId(entry, parent.entryId)))) {
          this.state.opened.push(parent.entryId);
        }
        this.openAllParent(parent);
      }
    }
  }

  private closeAllFirstChildren(item: ITreeViewDropDownOption): number[][] {
    // opene this item
    if (!isSet(this.state.opened.find((child) => this.equalEntryId(child, item.entryId)))) {
      this.state.opened.push(item.entryId);
    }

    // close all children
    return this.state.opened.filter(
      (openItem) => !this.isDirectChildOfParent(openItem, item.entryId),
    );
  }

  private optionsCallback(props: OptionProps<ITreeViewDropDownOption, boolean, any>) {
    if (this.shouldGetRendered(props.data) || this.state.inputText !== "") {
      return (
        <div className="treeview-entry px-2">
          <div
            onClick={(event) => {
              this.onChange(
                props.data.checked === HierarchySelectionState.SELECTED ?
                  "deselect-option"
                : "select-option",
                event,
                props.data,
                false,
              );
              // check if current item was selected -> open all parents to this entry
              if (props.data.checked === HierarchySelectionState.SELECTED) {
                this.openAllParent(props.data);
                this.setState({ opened: this.state.opened });
              }
            }}
          >
            <components.Option className="dropdownOption" {...props}>
              <div
                className="flex"
                style={{
                  marginLeft: `${
                    this.state.inputText !== "" ? 0 : props.data.entryId.length * 10
                  }px`,
                }}
              >
                {this.state.inputText === "" ?
                  props.data.hasChildren === true ?
                    !this.openedSet.has(props.data.entryId.join(",")) ?
                      <IconTreeMore
                        onClick={(event) => {
                          // update the statevar --> this will not update the component!
                          this.state.opened.push(props.data.entryId);

                          if (event.shiftKey === true) {
                            // open all children
                            this.props.options
                              .filter((entry) =>
                                this.isChildOfParent(entry.entryId, props.data.entryId),
                              )
                              .forEach((entry) => {
                                // update the statevar --> this will not update the component!
                                if (
                                  !isSet(
                                    this.state.opened.find((openId) =>
                                      this.equalEntryId(openId, entry.entryId),
                                    ),
                                  )
                                ) {
                                  this.state.opened.push(entry.entryId);
                                }
                              });
                          }

                          // now update the state itself by setting the opened statevar again
                          // --> this will update the component!
                          this.setState({ opened: this.state.opened });

                          event.preventDefault();
                          event.stopPropagation();
                        }}
                      />
                    : <IconTreeLess
                        onClick={(event) => {
                          let values = this.state.opened;

                          if (event.shiftKey === true) {
                            // close all sub-children (remove from opened list)
                            values = values.filter(
                              (entry) => !this.isChildOfParent(entry, props.data.entryId),
                            );
                          }

                          // remove the current entry from opened list
                          values = values.filter(
                            (entry) => !this.equalEntryId(entry, props.data.entryId),
                          );

                          // update the statevar --> this will update the component!
                          this.setState({ opened: values });

                          event.preventDefault();
                          event.stopPropagation();
                        }}
                      />

                  : props.data.isEndNode === false ?
                    <IconTreeMiddle />
                  : <IconTreeEnd />
                : <></>}
                <label
                  className="cursor-pointer ms-2 me-2"
                  style={{
                    color:
                      (
                        !isSet(this.state.inputText) ||
                        this.state.inputText === "" ||
                        props.data.text.toLowerCase().includes(this.state.inputText.toLowerCase())
                      ) ?
                        props.data.color
                      : "rgba(1,1,1,0.1)",
                  }}
                  title={this.props.renderToolTip?.(props.data)}
                >
                  {props.data.entryId.length === 1 ?
                    this.context.formatMessage({ id: props.data.text as string })
                  : props.data.text}
                </label>
              </div>
              {this.props.isHierarchyMode === true ?
                <div
                  className="flex"
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    this.onChange("select-option", event, props.data, true);
                    // check if current item was selected -> open all parents to this entry
                    if (props.data.checked === HierarchySelectionState.SELECTED) {
                      this.openAllParent(props.data);
                      this.setState({ opened: this.state.opened });
                    }
                  }}
                >
                  {props.data.checked === HierarchySelectionState.SELECTED ?
                    <IconDDCircleSelected className="ms-5" />
                  : props.data.checked === HierarchySelectionState.SOMESELECTED ?
                    <IconDDCircleMultiple />
                  : props.data.hasChildren ?
                    <IconDDCircleNone />
                  : <div className="ms-5"></div>}
                </div>
              : props.data.checked === HierarchySelectionState.SELECTED ?
                <IconDDSelSelected />
              : props.data.checked === HierarchySelectionState.UNSELECTED ?
                <IconDDSelNone />
              : <IconDDSelMultiple />}
            </components.Option>
          </div>
        </div>
      );
    }
  }

  Menu = (props: JSX.IntrinsicElements["div"]) => {
    const shadow = "hsla(218, 50%, 10%, 0.1)";
    return (
      <div
        className="shadow"
        style={{
          backgroundColor: "white",
          borderRadius: 6,
          boxShadow: `0 0 0 1px ${shadow}, 0 4px 24px ${shadow}`,
          marginTop: 8,
          position: "absolute",
          zIndex: cssContent.DROPDOWN_FOREGROUND_Z_INDEX,
        }}
        {...props}
      />
    );
  };

  Blanket = (props: JSX.IntrinsicElements["div"]) => (
    <div
      style={{
        bottom: 0,
        left: 0,
        top: 0,
        right: 0,
        position: "fixed",
        zIndex: cssContent.DROPDOWN_BUTTON_Z_INDEX,
      }}
      {...props}
    />
  );

  Dropdown = ({
    children,
    isOpen,
    target,
    onClose,
  }: {
    children?: React.ReactNode;
    readonly isOpen: boolean;
    readonly target: React.ReactNode;
    readonly onClose: () => void;
  }) => (
    <div className="me-2" style={{ position: "relative" }}>
      {target}
      {isOpen ?
        <this.Menu>{children}</this.Menu>
      : null}
      {isOpen ?
        <this.Blanket onClick={onClose} />
      : null}
    </div>
  );
}

const TreeViewDropDown = connect(mapStateToProps, mapDispatchToProps)(TreeViewDropDownComponent);

export default TreeViewDropDown;
